import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Show } from "@refinedev/antd";
import { useShow, useNavigation, useCustom } from "@refinedev/core";
import { Card, Descriptions, Table, Space, Button, List, Radio, Tabs, Alert, Tooltip } from "antd";
import { ArrowLeftOutlined, FileTextOutlined } from "@ant-design/icons";
import { SOURCING_ONE_QUERY, COUNT_BIDS_BY_SOURCING_QUERY, GET_BIDS_BY_SOURCING_QUERY, GET_PROCUREMENT_MINUTES_QUERY } from "./queries";
import { SubmitButton } from "./components/SubmitButton";
import { Sourcing, SourcingStatus, SourcingType } from "./interface";
import WorkflowDiagram from "../purchase-template/components/WorkflowDiagram";
import { ApprovalDetailsTable } from "../purchase-request/components/ApprovalDetailsTable";
import { SourcingFlow } from "./components/SourcingFlow";
import { StatusBadge, TypeBadge, ModeBadge } from "./components/Badges";
import { BidsTab } from "./components/BidsTab";
import { EvaluationTab } from "./components/EvaluationTab";
import { ResultTab } from "./components/ResultTab";
import { AwardTab } from "./components/AwardTab";
import { OverviewTab } from "./components/OverviewTab";
import { CreateMinuteDialog } from "./components/CreateMinuteDialog";
import { MinutesTab } from "./components/MinutesTab";
import { UserInfo } from "../../components/UserInfo";

// Status order for index-based visibility logic
const SOURCING_STATUS_ORDER = [
  SourcingStatus.DRAFT,
  SourcingStatus.SUBMITTED,
  SourcingStatus.APPROVED,
  SourcingStatus.PUBLISHED,
  SourcingStatus.CLOSED,
  SourcingStatus.REJECTED,
  SourcingStatus.RESULT_SUBMITTED,
  SourcingStatus.AWARD_APPROVED,
  SourcingStatus.PARTIALLY_AWARDED,
  SourcingStatus.AWARDED,
];



export const SourcingShow: React.FC = () => {
  const navigate = useNavigate();
  const { goBack } = useNavigation();

  const { query } = useShow<Sourcing>({
    resource: "sourcings",
    meta: { gqlQuery: SOURCING_ONE_QUERY },
  });

  const { data, isLoading } = query;
  const record = data?.data;

  // State for minute dialog
  const [isMinuteDialogVisible, setIsMinuteDialogVisible] = useState(false);

  // Calculate status index for simplified tab visibility logic
  const statusIndex = SOURCING_STATUS_ORDER.indexOf(record?.status || SourcingStatus.DRAFT);

  const [activeTabKey, setActiveTabKey] = useState<string>("overview");

  // Query for bid count - only enable when status is PUBLISHED or CLOSED
  const {data:bidCount,  refetch: refetchBidCount } = useCustom({
    url: '',
    method: 'get',
    meta: {
      gqlQuery: COUNT_BIDS_BY_SOURCING_QUERY,
      gqlVariables: {
        sourcingId: record?.id || '',
      },
    },
    queryOptions: {
      enabled: !!record?.id && statusIndex >= SOURCING_STATUS_ORDER.indexOf(SourcingStatus.PUBLISHED),
    },
  });

  
  // Query for bids - will be called only when button is clicked
  const { refetch: refetchBids } = useCustom({
    url: '',
    method: 'get',
    meta: {
      gqlQuery: GET_BIDS_BY_SOURCING_QUERY,
      gqlVariables: {
        sourcingId: record?.id || '',
      },
    },
    queryOptions: {
      enabled: false, // Never auto-execute, only call manually
    },
  });

  // Query for procurement minutes
  const { data: minutesData, isLoading: minutesLoading, refetch: refetchMinutes } = useCustom({
    url: '',
    method: 'get',
    meta: {
      gqlQuery: GET_PROCUREMENT_MINUTES_QUERY,
      gqlVariables: {
        sourcingId: record?.id || '',
      },
    },
    queryOptions: {
      enabled: !!record?.id,
    },
  });

  const procurementMinutes = minutesData?.data?.procurementMinutes?.nodes || [];


  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!record) {
    return <div>Sourcing not found</div>;
  }

  const bidColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
    },
    {
      title: "Unit",
      dataIndex: "unit",
      key: "unit",
    },
    {
      title: "Unit Price",
      dataIndex: "unitPrice",
      key: "unitPrice",
      render: (value: number) => `${record.currency || "ETB"} ${value?.toLocaleString()}`,
    },
    {
      title: "Total Price",
      dataIndex: "totalPrice",
      key: "totalPrice",
      render: (value: number) => `${record.currency || "ETB"} ${value?.toLocaleString()}`,
    },
  ];

  // Function to open minute dialog
  const openMinuteDialog = () => {
    setIsMinuteDialogVisible(true);
  };

  // Function to handle minute dialog success
  const handleMinuteDialogSuccess = () => {
    query.refetch();
    // Note: We don't need to explicitly refetch minutes data here
    // because the useCustom query for minutes has enabled: !!record?.id,
    // so it should refetch automatically when record is updated.
  };

  return (
    <Show
      title={record ? `Sourcing: ${record.sourcingNo}` : "Sourcing"}
      headerButtons={
        record && (
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={goBack}>
              Back
            </Button>
            {record.status === SourcingStatus.DRAFT && (
              <SubmitButton
                sourcingId={record.id}
                sourcing={record}
                currentStatus={record.status}
                onSuccess={() => query.refetch()}
              />
            )}
            {record.status === SourcingStatus.SUBMITTED && record.approvalType === 'LOCAL' && (
              <Button type="primary">
                Approve Sourcing
              </Button>
            )}
            {record.status === SourcingStatus.APPROVED && (
              <Button
                type="primary"
                onClick={() => navigate(`/sourcings/publish/${record.id}`)}
              >
                Publish to Portal
              </Button>
            )}
            {record.status === SourcingStatus.CLOSED && (
              <Button
                type="primary"
                icon={<FileTextOutlined />}
                onClick={openMinuteDialog}
              >
                Create Minute
              </Button>
            )}
          </Space>
        )
      }
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        {/* Sourcing Flow */}
        <SourcingFlow record={record} />

        {/* Bid Count Alert - Show only for PUBLISHED status */}
        {record && record.status === SourcingStatus.PUBLISHED && record.publishData && record.id && (() => {
          // Calculate deadline status and time information
          const isDeadlinePassed = record?.publishData?.dueDate
            ? new Date(record.publishData.dueDate) < new Date()
            : false;

          const getTimeUntilDeadline = () => {
            if (!record?.publishData?.dueDate) return null;

            const deadline = new Date(record.publishData.dueDate);
            const now = new Date();
            const timeDiff = deadline.getTime() - now.getTime();

            if (timeDiff <= 0) return null;

            const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

            let timeString = '';
            if (days > 0) {
              timeString = `${days} day${days > 1 ? 's' : ''} ${hours} hour${hours > 1 ? 's' : ''}`;
            } else if (hours > 0) {
              timeString = `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes > 1 ? 's' : ''}`;
            } else {
              timeString = `${minutes} minute${minutes > 1 ? 's' : ''}`;
            }

            return timeString;
          };

          const getDeadlineDateTime = () => {
            if (!record?.publishData?.dueDate) return null;
            const deadline = new Date(record.publishData.dueDate);
            return deadline.toLocaleString();
          };

          const timeUntilDeadline = getTimeUntilDeadline();
          const deadlineDateTime = getDeadlineDateTime();

          return (
            <Alert
              message={
                isDeadlinePassed
                  ? `${bidCount?.data?.countBidsBySourcing || 0} bids submitted`
                  : `${bidCount?.data?.countBidsBySourcing || 0} bids submitted - Opens ${deadlineDateTime} (in ${timeUntilDeadline})`
              }
              type="info"
              showIcon
              action={
                <Tooltip
                  title={
                    isDeadlinePassed
                      ? "Click to view all submitted bids"
                      : `Bids will be available on ${deadlineDateTime} (in ${timeUntilDeadline})`
                  }
                >
                  <Button
                    type="primary"
                    onClick={() => {
                      // Trigger the refetchBids function to load bids
                      if (refetchBids) {
                        refetchBids();
                      }
                      // Switch to bids tab
                      setActiveTabKey("bids");
                    }}
                    disabled={!isDeadlinePassed}
                  >
                    View Details
                  </Button>
                </Tooltip>
              }
            />
          );
        })()}

        <Tabs activeKey={activeTabKey} onChange={setActiveTabKey} type="card">
          {/* Overview Tab */}
          <Tabs.TabPane tab="Overview" key="overview">
            <OverviewTab record={record} />
          </Tabs.TabPane>
          
          {/* Basic Information Tab */}
          <Tabs.TabPane tab="Basic Information" key="basic">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Card title="Basic Information">
                <Descriptions column={2}>
                  <Descriptions.Item label="Sourcing No">{record.sourcingNo}</Descriptions.Item>
                  <Descriptions.Item label="PR References">
                    {record.prIds && record.prIds.length > 0 ? (
                      <ul style={{ margin: 0, paddingLeft: '20px' }}>
                        {record.prIds.map(prId => (
                          <li key={prId}>{prId}</li>
                        ))}
                      </ul>
                    ) : (
                      'N/A'
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Type">
                    <TypeBadge type={record.type} />
                  </Descriptions.Item>
                  <Descriptions.Item label="Invitation Mode">
                    <ModeBadge mode={record.invitationMode} />
                  </Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <StatusBadge status={record.status} />
                  </Descriptions.Item>
                  <Descriptions.Item label="Total Amount">
                    {record.currency || "ETB"} {record.totalAmount?.toLocaleString()}
                  </Descriptions.Item>
                   <Descriptions.Item label="Created By">
                     {record.createdBy ? <UserInfo userId={record.createdBy} /> : "N/A"}
                   </Descriptions.Item>
                  <Descriptions.Item label="Created Date">
                    {new Date(record.createdAt).toLocaleDateString()}
                  </Descriptions.Item>
                   {record.submittedBy && (
                     <Descriptions.Item label="Submitted By">
                       <UserInfo userId={record.submittedBy} />
                     </Descriptions.Item>
                   )}
                   {record.approvedBy && (
                     <Descriptions.Item label="Approved By">
                       <UserInfo userId={record.approvedBy} />
                     </Descriptions.Item>
                   )}
                </Descriptions>
                {record.title && (
                  <div style={{ marginTop: 16 }}>
                    <strong>Title:</strong>
                    <p>{record.title}</p>
                  </div>
                )}
                {record.description && (
                  <div style={{ marginTop: 8 }}>
                    <strong>Description:</strong>
                    <p>{record.description}</p>
                  </div>
                )}
              </Card>

              <Card title="Approval Configuration">
                <Descriptions column={1}>
                  <Descriptions.Item label="Approval Type">
                    <Radio.Group value={record.approvalType} disabled>
                      <Radio value="LOCAL">Local Approval</Radio>
                      <Radio value="EXTERNAL">External Approval Flow</Radio>
                    </Radio.Group>
                  </Descriptions.Item>
                </Descriptions>
                {record.approvalType === 'EXTERNAL' && record.externalWorkflow && (
                  <div style={{ marginTop: 16 }}>
                    <WorkflowDiagram flow={record.externalWorkflow} />
                  </div>
                )}
              </Card>

              <Card title="Bid Details">
                <Table
                  dataSource={record.items}
                  columns={bidColumns}
                  rowKey="id"
                  pagination={false}
                  summary={() => (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={5}>
                        <strong>Total</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1}>
                        <strong>
                          {record.currency || "ETB"} {record.totalAmount?.toLocaleString()}
                        </strong>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                />
                {record.notes && (
                  <div style={{ marginTop: 16 }}>
                    <strong>Notes:</strong>
                    <p>{record.notes}</p>
                  </div>
                )}
              </Card>

              {/* Attachments */}
              {record.attachments && record.attachments.length > 0 && (
                <Card title="Attachments">
                  <List
                    dataSource={record.attachments}
                    renderItem={(attachment: any) => (
                      <List.Item
                        actions={[
                          <Button
                            key="download"
                            type="link"
                            icon={<FileTextOutlined />}
                            href={attachment.url}
                            target="_blank"
                          >
                            View/Download
                          </Button>
                        ]}
                      >
                        <List.Item.Meta
                          title={attachment.originalName}
                          description={`${(attachment.size / 1024).toFixed(2)} KB • ${attachment.category || 'Other'}`}
                        />
                      </List.Item>
                    )}
                  />
                </Card>
              )}

              {/* Evaluation Criteria - Only show for RFP */}
              {record.type === SourcingType.RFP && record.evaluationCriterias && record.evaluationCriterias.length > 0 && (
                <Card title="Evaluation Criteria">
                  <Table
                    dataSource={record.evaluationCriterias}
                    rowKey="id"
                    pagination={false}
                    columns={[
                      {
                        title: 'Name',
                        dataIndex: 'name',
                        key: 'name',
                      },
                      {
                        title: 'Description',
                        dataIndex: 'description',
                        key: 'description',
                      },
                      {
                        title: 'Weight (%)',
                        dataIndex: 'weight',
                        key: 'weight',
                        render: (value: number) => `${value}%`,
                      },
                      {
                        title: 'Visible to Suppliers',
                        dataIndex: 'visibleToSuppliers',
                        key: 'visibleToSuppliers',
                        render: (value: string) => {
                          switch (value) {
                            case 'FULL':
                              return 'Full';
                            case 'PARTIAL':
                              return 'Partial (hide weights/threshold)';
                            case 'NONE':
                              return 'None';
                            default:
                              return value;
                          }
                        },
                      },
                      {
                        title: 'Threshold',
                        dataIndex: 'threshold',
                        key: 'threshold',
                        render: (value?: number) => value ? value.toString() : 'N/A',
                      },
                    ]}
                    summary={() => (
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={2}>
                          <strong>Total Weight</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1}>
                          <strong>
                            {record.evaluationCriterias?.reduce((sum, criteria) => sum + (criteria.weight || 0), 0)}%
                          </strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2} colSpan={2}></Table.Summary.Cell>
                      </Table.Summary.Row>
                    )}
                  />
                </Card>
              )}

              {/* Approval Details */}
              {(record.approvals && record.approvals.length > 0) && (
                <ApprovalDetailsTable approvals={record.approvals} />
              )}
            </Space>
          </Tabs.TabPane>

          {/* Bids Tab - Show for PUBLISHED and later statuses */}
          {statusIndex >= SOURCING_STATUS_ORDER.indexOf(SourcingStatus.PUBLISHED) && (
            <Tabs.TabPane  tab={
                record?.publishData?.openedAt
                  ? `Bids (Opened - ${record?.publishData?.bids?.length || 0} submitted)`
                  : `Bids (${bidCount?.data?.countBidsBySourcing || 0} submitted)`
              } key="bids">
              <BidsTab
                record={record}
                query={query}
                refetchBidCount={refetchBidCount}
                refetchBids={refetchBids}
              />
            </Tabs.TabPane>
          )}

          {/* Evaluation Tab - Show for CLOSED and later statuses */}
          {statusIndex >= SOURCING_STATUS_ORDER.indexOf(SourcingStatus.CLOSED) && (
            <Tabs.TabPane tab="Evaluation" key="evaluation">
              <EvaluationTab record={record} />
            </Tabs.TabPane>
          )}


       
          {/* Result Tab - Show for CLOSED and later statuses */}
          {statusIndex >= SOURCING_STATUS_ORDER.indexOf(SourcingStatus.CLOSED) && (
            <Tabs.TabPane tab="Result" key="result">
              <ResultTab record={record} />
            </Tabs.TabPane>
          )}

          {/* Award Tab - Show for AWARD_APPROVED status */}
          {statusIndex >= SOURCING_STATUS_ORDER.indexOf(SourcingStatus.AWARD_APPROVED) && (
            <Tabs.TabPane tab="Award" key="award">
              <AwardTab record={record} onRefetch={query.refetch} />
            </Tabs.TabPane>
          )}

          {/* Minutes Tab - Show for all statuses */}
          <Tabs.TabPane 
            tab={`Minutes (${procurementMinutes.length})`} 
            key="minutes"
          >
            <MinutesTab minutes={procurementMinutes} onRefetch={refetchMinutes} />
          </Tabs.TabPane>
        </Tabs>

        {/* Create Minute Dialog */}
        <CreateMinuteDialog
          visible={isMinuteDialogVisible}
          onCancel={() => setIsMinuteDialogVisible(false)}
          sourcingId={record.id}
          sourcingTitle={record.title || "Untitled Sourcing"}
          sourcingType={record.type}
          invitedSuppliers={record.publishData?.invitedSuppliers || []}
          openedAt={record.openedAt}
          publishedDate={record.publishData?.createdAt}
          submittedBids={record.publishData?.bids || []}
          onSuccess={handleMinuteDialogSuccess}
        />

      </Space>
    </Show>
  );
};
